import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import "./styles/ThoughtCard.css";
import axios from "axios";
import firebase from "../../firebase";
import { formatDate } from "../helpers/helpers";
import CreatableSelect from "react-select/creatable";
import { MultiValue } from "react-select";
import Output from "editorjs-react-renderer";

interface Thought {
  id: string;
  date: string;
  [key: string]: any;
}

interface ThoughtCardProps {
  fetchThoughts: () => void;
  thought: Thought;
  allLabels: string[];
}

type SelectChangeValueType = Array<any> | null;

const ThoughtCard: React.FC<ThoughtCardProps> = ({
  fetchThoughts,
  thought,
  allLabels,
}) => {
  const [date, setDate] = useState<string[]>(["", ""]);
  const [edit, setEdit] = useState<boolean>(false);
  const [editContent, setEditContent] = useState<Thought>(thought);
  const [toggle, setToggle] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<any>(null);

  const stringLabels = (thought: Thought) => {
    // copy thought object
    let thought_copy = { ...thought };
    thought_copy["preview"] = JSON.stringify(thought_copy["preview"]);
    thought_copy["content"] = JSON.stringify(thought_copy["content"]);
    setEditContent(thought_copy);
  };

  useEffect(() => {
    stringLabels(thought);
    if (thought.date) setDate(formatDate(thought.date));
  }, [thought]);

  const handleEditChange = (
    key: string,
    selectedOptions:
      | SelectChangeValueType
      | ChangeEvent<HTMLInputElement>
      | MultiValue<any>
      | undefined
  ) => {
    let selectedLabels: string = "";
    if (key === "label") {
      if (
        !selectedOptions ||
        !(selectedOptions as SelectChangeValueType)?.length
      ) {
        return;
      }
      let iterator: SelectChangeValueType;
      iterator = (selectedOptions as SelectChangeValueType) ?? [];
      if (selectedOptions) {
        selectedLabels = iterator.map((option) => option.value).join(", ");
      }
      setEditContent({
        ...editContent,
        [key]: selectedLabels,
      });
    } else {
      setEditContent({
        ...editContent,
        [key]: (selectedOptions as ChangeEvent<HTMLInputElement>).target.value,
      });
    }
    return null;
  };

  const handleAddField = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const values = editContent;
    if (!inputRef?.current?.value) return;
    values[inputRef.current.value] = "";
    setEditContent(values);
    setToggle(false);
  };

  const addBtnClick = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setToggle(true);
  };

  const handleEdit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const user = firebase.auth().currentUser;
      const token = user && (await user.getIdToken());

      await axios.put(
        "https://us-central1-thought-cabinet.cloudfunctions.net/app/updateThought",
        {
          ...editContent,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchThoughts();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errResp = error.response;
        console.error("Error editing thought:", errResp);
      }
    }
    setEdit(false);
  };

  const handleDelete = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      const user = firebase.auth().currentUser;
      const token = user && (await user.getIdToken());

      const res = await axios.delete(
        "https://us-central1-thought-cabinet.cloudfunctions.net/app/deleteThought",
        {
          data: {
            id: thought.id,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchThoughts();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errResp = error.response;
        console.error("Error deleting thought:", errResp);
      }
    }
  };

  return (
    <div key={thought.id} className="thought-card">
      <div className="edit-container">
        <button
          className="edit-button"
          onClick={(e) => {
            setEdit(!edit);
          }}
        >
          {edit ? "Cancel" : "Edit"}
        </button>
      </div>
      {edit && (
        <>
          <form className="edit-form" onSubmit={(e) => handleEdit(e)}>
            {Object.keys(editContent).map((key) => {
              return (
                <div key={key}>
                  <label htmlFor={key}>{key}</label>
                  {key === "label" ? (
                    <>
                      <CreatableSelect
                        isClearable
                        isMulti
                        options={allLabels.map((label) => ({
                          value: label,
                          label: label,
                        }))}
                        value={editContent[key]
                          .split(", ")
                          .map((label: string) => ({
                            value: label,
                            label: label,
                          }))}
                        onChange={(selectedOptions) => {
                          handleEditChange(key, selectedOptions);
                        }}
                      />
                    </>
                  ) : (
                    <input
                      type="text"
                      name={key}
                      value={editContent[key]}
                      onChange={(e) => handleEditChange(key, e)}
                    />
                  )}
                </div>
              );
            })}

            {!toggle ? (
              <div className="center">
                <button className="edit-button" onClick={addBtnClick}>
                  Add new
                </button>
              </div>
            ) : (
              <div className="dialog-box">
                <input type="text" placeholder="label" ref={inputRef} />
                <select ref={selectRef}>
                  <option value="text">Text</option>
                </select>
                <button className="edit-buttom" onClick={handleAddField}>
                  Add
                </button>
              </div>
            )}

            <button type="submit" className="submit-button">
              Submit
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                handleDelete(e);
              }}
            >
              Delete
            </button>
          </form>
        </>
      )}
      <div className="thought-header">
        <span className="thought-date"> {date[0]}</span>
        <span className="thought-time"> {date[1]}</span>
      </div>
      <div className="thought-content">
        {thought.type === "image" ? (
          <>
            <img
              src={thought.thumbnailUrl ? thought.thumbnailUrl : thought.url}
              alt="Thought"
              className="thought-image"
            />
            <Output data={thought.caption} />
          </>
        ) : thought.type === "text" ? (
          <Output data={thought.content} />
        ) : thought.type === "link" ? (
          <a href={thought.url}>
            <img
              src={thought.thumbnailUrl ? thought.thumbnailUrl : thought.imgUrl}
              alt={"Image preview for " + thought.title}
              className="thought-image"
            />
            <div className="thought-link-text">
              <div className="thought-link-title">{thought.title}</div>
              <Output data={thought.description} />
            </div>
          </a>
        ) : thought.type === "rich_text" ? (
          <section>
            <Output data={thought.content} />
          </section>
        ) : null}
      </div>
      {
        <div className="thought-labels">
          {thought.label &&
            thought.label.split(", ").map((label: string) => (
              <span key={label} className="thought-label">
                {label}
              </span>
            ))}
        </div>
      }
    </div>
  );
};

export default ThoughtCard;
